<template>
  <nav class="flex flex-col gap-0.5 px-2 py-4">
    <template v-for="(group, gi) in navGroups" :key="gi">
      <!-- Group labels are headings, not links: small, the second ink,
           sentence case — a tracked-out capital label is chrome shouting. -->
      <div
        v-if="group.label"
        class="px-2.5 pt-4 pb-1.5 text-[11px] font-semibold text-muted-foreground"
      >
        {{ group.label }}
      </div>
      <!-- Every row is the page ink at full contrast (a dimmed row read as
           disabled). ⭐ THE CURRENT PAGE IS A DISC (his pick, 2026-10-10): the
           glyph sits in a 24 px round of the STATE teal with its label in the
           same teal — the mark's own disc moved into the menu, « you are here »
           and nothing else. No wash, no band at the edge (« classic AI
           design »). Hover is the sunk grey on the other rows; the current row
           has none — there is nowhere to go. -->
      <template v-for="item in group.items" :key="item.href">
        <router-link
          :to="item.href"
          class="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-foreground transition-colors"
          :class="[
            isActive(item)
              ? 'cursor-default font-semibold text-state-ink'
              : 'font-medium hover:bg-sunken'
          ]"
          :data-testid="`nav-item-${item.key}`"
          @click="emit('navigate')"
        >
          <!-- A parent whose CHILD is current is lit without the disc: the
               disc says « this page », and the page is the child's. -->
          <span
            v-if="isActive(item) && !item.children"
            class="-my-1 -ml-1 grid size-6 shrink-0 place-items-center rounded-full bg-state text-state-foreground"
            data-testid="nav-current-disc"
          >
            <component :is="item.icon" class="size-[13px]" :stroke-width="2.5" />
          </span>
          <component :is="item.icon" v-else class="size-4 shrink-0" />
          {{ item.title }}
        </router-link>
        <!-- ★ SUB-ITEMS (the pipeline's three pages): indented under the
             parent, always visible, no icon — the parent's glyph names the
             family. The current child wears the state teal and a dot where
             the parent's disc would be; a badge pill sits at the right, on the
             sunken plane (teal when current), hidden at zero, pulsing while
             its number is awaited. -->
        <router-link
          v-for="child in (item.children ?? []).filter(c => !c.hidden)"
          :key="child.href"
          :to="child.href"
          class="flex items-center gap-2.5 rounded-md py-1.5 pl-9 pr-2.5 text-sm text-foreground transition-colors"
          :class="isChildActive(child) ? 'cursor-default font-semibold text-state-ink' : 'font-medium hover:bg-sunken'"
          :data-testid="`nav-child-${child.key}`"
          @click="emit('navigate')"
        >
          <span
            v-if="isChildActive(child)"
            class="-ml-4 size-1.5 shrink-0 rounded-full bg-state"
            data-testid="nav-current-dot"
          />
          <span class="truncate">{{ child.title }}</span>
          <span
            v-if="child.badge !== undefined && child.badge !== 0"
            :class="['ml-auto inline-grid h-5 min-w-[22px] place-content-center rounded-full px-1.5 text-xs font-bold tabular-nums',
                     isChildActive(child) ? 'bg-state text-state-foreground' : 'bg-sunken text-foreground',
                     child.badge === null ? 'animate-pulse' : '']"
            :data-testid="`nav-badge-${child.key}`"
          >{{ child.badge ?? '' }}</span>
        </router-link>
      </template>
    </template>
  </nav>
</template>

<script setup lang="ts">
import { computed, watchEffect, type Component } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useOrganizationStore } from '@/stores/organization'
import { usePermissions } from '@/composables/usePermissions'
import { usePipelineBadges } from '@/composables/usePipelineBadges'
import { useSeasonCatalog } from '@/composables/useSeasonCatalog'
import { pageBadge } from '@/lib/salesReasons'
import {
  Users,
  Briefcase,
  FileText,
  BookOpen,
  Building2,
  MapPin,
  Truck,
  Sun,
  ClipboardList,
  Route,
  Inbox,
  TrendingUp,
  Upload,
} from 'lucide-vue-next'

const emit = defineEmits<{
  (e: 'navigate'): void
}>()

const { t } = useI18n()
const currentRoute = useRoute()
const orgStore = useOrganizationStore()
const { isAdmin } = usePermissions()
const badges = usePipelineBadges()
const catalog = useSeasonCatalog()

interface NavChild {
  key: string
  title: string
  href: string
  /** undefined = no badge on this page; null = awaited; a number = the count. */
  badge?: number | null
  hidden?: boolean
}

interface NavItem {
  key: string
  title: string
  href: string
  icon: Component
  // Extra path prefixes that keep this entry lit (a tabbed page spanning
  // several routes).
  also?: string[]
  children?: NavChild[]
}

// Every entry owns a distinct PATH again — the funnel desk left /planning for
// /pipeline, so the query-aware variant this needed is gone with it.
const isActive = (item: NavItem) =>
  currentRoute.path === item.href ||
  (item.href !== '/' && currentRoute.path.startsWith(item.href)) ||
  (item.also ?? []).some(prefix => currentRoute.path.startsWith(prefix))
const isChildActive = (child: NavChild) =>
  currentRoute.path === child.href || currentRoute.path.startsWith(`${child.href}/`)

// The sidebar outlives the pages, so it owns the badges' live subscription
// and the org-wide read; the season catalog decides whether Renouvellements
// is drawn at all (a shop with no declarable season has no campaign).
badges.watchLive()
watchEffect(() => {
  const orgId = orgStore.currentOrganization?.id
  if (!orgId || !orgStore.salesEnabled) return
  badges.ensure(orgId)
  void catalog.load(orgId)
})
const hasDeclarableSeason = computed(() => catalog.periods.value.some(p => p.attributes.nature === 'declarable'))

const navGroups = computed<{ label: string; items: NavItem[] }[]>(() => [
  {
    label: '',
    items: [
      { key: 'planning', title: t('planning.title'), href: '/planning', icon: Route },
    ],
  },
  // VENTES — a lead is a JOB in its early phase, but it is SALES work, so it
  // gets its own group. « Pipeline » is the desk (what do I do today), in
  // THREE PAGES (his ruling): Ventes (the sales queue), Renouvellements (the
  // campaign — a cohort of customers, never a queue), Paiements (what is
  // owed). « Leads » is the list (the whole lifetime, including the dead
  // ones), and a soumission is a sales document — it never belonged under
  // OPÉRATIONS. The whole group exists only for a shop that quotes (the org's
  // `sales_enabled` switch); a shop that only schedules work has no Ventes.
  ...(orgStore.salesEnabled ? [ {
    label: t('nav.sales'),
    items: [
      {
        key: 'pipeline', title: t('nav.salesDesk'), href: '/pipeline', icon: TrendingUp,
        children: [
          { key: 'sales', title: t('salesFlow.page.sales'), href: '/pipeline/sales', badge: pageBadge(badges.meta.value, 'sales') },
          {
            key: 'renewals', title: t('salesFlow.page.renewals'), href: '/pipeline/renewals',
            badge: badges.meta.value ? badges.meta.value.campaigns.to_call_customers : null,
            hidden: !hasDeclarableSeason.value,
          },
          { key: 'payments', title: t('salesFlow.page.payments'), href: '/pipeline/payments', badge: pageBadge(badges.meta.value, 'payments') },
        ],
      },
      // /leads AND /leads/:id — the sales door onto a deal keeps Ventes lit.
      { key: 'leads', title: t('leads.title'), href: '/leads', icon: Inbox },
      { key: 'proposals', title: t('proposals.title'), href: '/proposals', icon: FileText },
    ],
  } ] : []),
  {
    label: t('nav.operations'),
    items: [
      { key: 'jobs', title: t('common.jobs'), href: '/jobs', icon: Briefcase },
    ],
  },
  {
    label: t('nav.clients'),
    items: [
      { key: 'contacts', title: t('common.contacts'), href: '/contacts', icon: Users },
      { key: 'companies', title: t('common.companies'), href: '/companies', icon: Building2 },
      { key: 'locations', title: t('serviceLocations.navTitle'), href: '/locations', icon: MapPin },
    ],
  },
  // RESSOURCES — the org's standing configuration: the fleet, the work
  // taxonomy, the price book. Daily work stays above; this band is what the
  // business IS, not what it does today. ⚰ « Assemblages » is gone: reuse
  // lives in the job type's offers, and a line is typed into the document.
  {
    label: t('nav.resources'),
    items: [
      { key: 'fleet', title: t('fleet.title'), href: '/fleet', icon: Truck },
      // Flotte = who, Saisons = when: both are the shop's state, not its setup.
      { key: 'seasons', title: t('workPeriods.title'), href: '/seasons', icon: Sun },
      { key: 'templates', title: t('templates.title'), href: '/templates', icon: ClipboardList },
      { key: 'parts', title: t('catalog.title'), href: '/parts', icon: BookOpen },
      // A tool's run history, not a setting. Importing writes the org's
      // records in bulk: admins and owners only (the API 404s anyone else).
      ...(isAdmin.value ? [ { key: 'imports', title: t('settings.tabs.imports'), href: '/imports', icon: Upload } ] : []),
    ],
  },
])
</script>
